import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../services/apiservice/apiservice.service';
import { ConfigService } from '../../services/config/config.service';
import { Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { INovelist } from '../../core/interfaces/novelist.interface';
import { IGetListByItemId } from '../your-list/get-list-by-item-id.interface';
import { PlaceHoldRequest, ReserveItemsModel } from '../hold/hold.service';
import { ApiResponse } from '../../services/api-response.interface';

export interface IGetByListItemIdRequest {
  listItemId: number;
  librarydbpoolkey: number;
  batchILL: boolean;
}

export interface IGetByAgcnIdRequest {
  agcontrolid: string;
  librarydbpoolkey: number;
  hldStartRecord?: number;
  hldNumOfRecords?: number;
  batchILL: boolean;
}

export interface IGetByShardRequest {
  source: string;
  agcontrolid: string;
  hldStartRecord: number;
  hldNumOfRecords: number;
}

export enum DIRECTION {
  LEFT,
  RIGHT,
}

export enum ACTION {
  APPEND,
  PREPEND,
}

export enum FULL_RECORD_OPTION_TABS {
  Details = 0,
  Where_To_Find = 1,
  MARC_Display = 2,
  More_About_This_Title = 3,
  GoodreadsReviews = 4,
  Syndetics_Unbound = 5,
  Novelist = 6,
  Shelf_Browse = 7,
  CLCD = 8,
}

@Injectable()
export class DetailsService {
  private _apiService = inject(ApiService);
  private _configService = inject(ConfigService);
  private _http = inject(HttpClient);
  private _router = inject(Router);

  readonly HOLDING_START_RECORD = 1;
  readonly HOLDING_NUM_OF_RECORD = 1000;
  readonly FULLRECORDSETTING_TIMEOUT = 300;
  readonly SYNDETICS_TIMEOUT = 500;
  readonly SHELF_PAGE_SIZE: number = 25;

  url = '';
  uri: string;

  updateHoldingsDetails(key): Observable<any> {
    this.url = `${this._configService.searchApi}/holding/details/${key}`;
    return this._apiService.get(this.url);
  }

  placeHold(data: PlaceHoldRequest): Observable<ApiResponse<ReserveItemsModel>> {
    this.url = this._configService.agApi + '/ils/placeHold';
    return this._apiService.post(this.url, data);
  }

  //   -------------------------------------------------------------------------
  //   ------------------------------ ADD TO DOWNLOAD CART SERVICE---------------------------------
  //   ---------------------------------------------------------------------------

  listAddItemToCart(data): Observable<any> {
    this.url = this._configService.agApi + '/List/ListAddItemToCart';
    return this._apiService.post(this.url, data);
  }

  async listAddItemToCartByAgcnAsync(post_body) {
    try {
      const url = `${this._configService.agApi}/List/listAddItemToCartByAgcn`;
      const response: any = await this._http.post(url, post_body).toPromise();
      if (response) {
        return response;
      }
    } catch (error) {
      console.error(`detailsService.ts:listAddItemToCartByAgcnAsync => ${error.message}`);
    }
    return null;
  }

  //--------------------------------------------------------------------------------
  // ------------------------ BX api calls ------------------------------------------
  // --------------------------------------------------------------------------------

  getFullDetailsBX(recordId): Observable<any> {
    this.url = this._configService.agApi + '/econtent/bt/GetFullDetails?titleId=' + recordId;
    return this._apiService.get(this.url);
  }

  checkoutBX(data): Observable<any> {
    this.url = this._configService.agApi + '/econtent/bt/Checkout';
    return this._apiService.post(this.url, data);
  }

  addHoldBX(data): Observable<any> {
    this.url = this._configService.agApi + '/econtent/bt/AddHold';
    return this._apiService.post(this.url, data);
  }

  removeHoldBX(data): Observable<any> {
    this.url = this._configService.agApi + '/econtent/bt/RemoveHold';
    return this._apiService.post(this.url, data);
  }

  //   --------------------------------------------------------------------------------
  //   ------------------------ Zinio CIRCULATION API CALLS -----------------------
  //   --------------------------------------------------------------------------------

  getZNCirculation(baseUrl, data): Observable<any> {
    this.url = baseUrl + '/AGEContentServices/json/oneway/ZinioItemDetailRequest';
    return this._apiService.post(this.url, data);
  }

  checkoutTitleZN(baseUrl, data): Observable<any> {
    this.url = baseUrl + '/AGEContentServices/json/oneway/ZinioCheckoutIssueRequest';
    return this._apiService.post(this.url, data);
  }

  registerUserZN(baseUrl, data): Observable<any> {
    this.url = baseUrl + '/AGEContentServices/json/oneway/ZinioCreatePatronRequest';
    return this._apiService.post(this.url, data);
  }

  async loginUserZNAsync(baseUrl, data) {
    this.url = baseUrl + '/AGEContentServices/json/oneway/ZinioLoginRequest';
    const response: any = await this._http.post(this.url, data).toPromise();
    if (response) {
      return response;
    }
    return null;
  }

  //   ------------------------------------------------------------------------------
  //   ----------------------------- SHELF BROWSE -----------------------------------
  //   ------------------------------------------------------------------------------

  shelfBrowse(data): Observable<any> {
    this.url = this._configService.searchApi + '/Browse/ShelfBrowse';
    return this._apiService.post(this.url, data);
  }

  getFullRecordByShard(payload) {
    this.url = `${this._configService.searchApi}/fullrecord`;
    return this._apiService.post(this.url, payload).pipe(
      map((response) => {
        if (response) {
          return response;
        } else {
          throw new Error('Unable to fetch Full Record.');
        }
      }),
    );
  }

  getFullRecordByAgcn(payload) {
    this.url = `${this._configService.searchApi}/fullrecord/GetByAGCN`;
    return this._apiService.post(this.url, payload).pipe(
      map((response) => {
        if (response) {
          return response;
        } else {
          throw new Error('Unable to fetch Full Record.');
        }
      }),
    );
  }

  getFullRecordByListItemId(payload: IGetListByItemId) {
    this.url = `${this._configService.searchApi}/fullrecord/GetByListItemId`;
    return this._apiService.post(this.url, payload).pipe(
      map((response) => {
        if (response) {
          return response;
        } else {
          throw new Error('Unable to fetch Full Record.');
        }
      }),
    );
  }

  //   --------------------------------------------------------------------------------
  //   ------------------------ RECORDED BOOKS API CALLS ------------------------------
  //   --------------------------------------------------------------------------------

  getRB(baseUrl, data): Observable<any> {
    this.url = baseUrl + '/AGEContentServices/json/oneway/RBItemFullDetailsRequest';
    return this._apiService.post(this.url, data);
  }

  placeHoldRB(baseUrl, data): Observable<any> {
    this.url = baseUrl + '/AGEContentServices/json/oneway/RBHoldItemsRequest';
    return this._apiService.post(this.url, data);
  }

  deleteHoldRB(baseUrl, data): Observable<any> {
    this.url = baseUrl + '/AGEContentServices/json/oneway/RBDeleteHoldRequest';
    return this._apiService.post(this.url, data);
  }

  checkoutNowRB(baseUrl, data): Observable<any> {
    this.url = baseUrl + '/AGEContentServices/json/oneway/RBCheckoutItemRequest';
    return this._apiService.post(this.url, data);
  }

  getCheckoutItemDetailsRB(baseUrl, data): Observable<any> {
    this.url = baseUrl + '/AGEContentServices/json/oneway/RBIsItemCheckedoutRequest';
    return this._apiService.post(this.url, data);
  }

  renewNowRB(baseUrl, data): Observable<any> {
    this.url = baseUrl + '/AGEContentServices/json/oneway/RBRenewItemRequest';
    return this._apiService.post(this.url, data);
  }

  returnNowRB(baseUrl, data): Observable<any> {
    this.url = baseUrl + '/AGEContentServices/json/oneway/RBCheckinRBItemRequest';
    return this._apiService.post(this.url, data);
  }

  registerUserRB(baseUrl, data): Observable<any> {
    this.url = baseUrl + '/AGEContentServices/json/oneway/RBCreateRBPatronRequest';
    return this._apiService.post(this.url, data);
  }

  //   --------------------------------------------------------------------------------
  //   ------------------------ NOVELIST API CALLS ------------------------------------
  //   --------------------------------------------------------------------------------

  getNovelistCredentials(): Observable<INovelist> {
    this.url = this._configService.agApi + '/Econtent/Novelist';
    return this._apiService.get(this.url);
  }

  getAppeals(data): Observable<any> {
    this.url = this._configService.agApi + '/Econtent/NovelistGetAppeals';
    return this._apiService.post(this.url, data);
  }

  buildRequestPayloadFullRecordByListItemId(
    listItemId,
    libraryDbPoolKey,
    isBatchILL,
  ): IGetByListItemIdRequest {
    return {
      listItemId: listItemId,
      librarydbpoolkey: libraryDbPoolKey,
      batchILL: isBatchILL,
    };
  }

  buildRequestPayloadFullRecordByAgcn(
    agControlId: string,
    libraryDbPoolKey: number,
    isBatchILL: boolean,
  ): IGetByAgcnIdRequest {
    return {
      agcontrolid: agControlId,
      librarydbpoolkey: libraryDbPoolKey,
      batchILL: isBatchILL,
    };
  }

  buildRequestPayloadFullRecordByShard(agControlId: string, source: string): IGetByShardRequest {
    return {
      source: source,
      agcontrolid: agControlId,
      hldStartRecord: this.HOLDING_START_RECORD,
      hldNumOfRecords: this.HOLDING_NUM_OF_RECORD,
    };
  }
}
